/**
 * Utility functions for generating unique keys in React components
 * Helps prevent duplicate key warnings and ensures proper component identity
 */

/**
 * Generates a unique key for conversation items
 * @param conversationId - The conversation ID
 * @param index - Optional index for additional uniqueness
 * @returns Unique key string
 */
export const generateConversationKey = (conversationId: number, index?: number): string => {
  return index !== undefined ? `conv-${conversationId}-${index}` : `conv-${conversationId}`;
};

/**
 * Generates a unique key for message items
 * @param messageId - The message ID
 * @param index - Optional index for additional uniqueness
 * @returns Unique key string
 */
export const generateMessageKey = (messageId: number, index?: number): string => {
  return index !== undefined ? `msg-${messageId}-${index}` : `msg-${messageId}`;
};

/**
 * Generates a unique key for file items
 * @param messageId - The parent message ID
 * @param file - The file object
 * @param index - The file index within the message
 * @returns Unique key string
 */
export const generateFileKey = (messageId: number, file: any, index: number): string => {
  const fileId = file.id || file.name || `unknown-${index}`;
  return `msg-${messageId}-file-${fileId}-${index}`;
};

/**
 * Generates a unique key for message group (date-based grouping)
 * @param date - The date string
 * @param messageCount - Number of messages in the group
 * @returns Unique key string
 */
export const generateMessageGroupKey = (date: string, messageCount: number): string => {
  return `date-${date}-count-${messageCount}`;
};

/**
 * Removes duplicate items from an array based on a key function
 * @param items - Array of items
 * @param keyFn - Function to extract unique key from each item
 * @returns Array with duplicates removed
 */
export const removeDuplicates = <T>(items: T[], keyFn: (item: T) => string | number): T[] => {
  const seen = new Set<string | number>();
  return items.filter(item => {
    const key = keyFn(item);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

/**
 * Validates that all keys in an array are unique
 * Logs warnings in development if duplicates are found
 * @param items - Array of items
 * @param keyFn - Function to extract key from each item
 * @param context - Context string for debugging
 */
export const validateUniqueKeys = <T>(
  items: T[], 
  keyFn: (item: T) => string | number, 
  context: string = 'items'
): void => {
  if (__DEV__) {
    const keys = items.map(keyFn);
    const uniqueKeys = new Set(keys);
    
    if (keys.length !== uniqueKeys.size) {
      const duplicates = keys.filter((key, index) => keys.indexOf(key) !== index);
      console.warn(`[KeyUtils] Duplicate keys found in ${context}:`, [...new Set(duplicates)]);
    }
  }
};
